'use client';

import { useMemo } from 'react';

import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';

import { getMessageMediaSize } from '../../functions/getMessageMediaSize';
import type { ChatPictureMessage, ImageContent } from '../../types/ChatMessage';
import { MessageBubbleShell } from './MessageBubbleShell';

interface Props {
  message: ChatPictureMessage;
  isOwn: boolean;
}

/**
 * Image bubble. Aspect-ratio-preserving size from the content's
 * intrinsic dimensions, capped at 320px on the larger edge. Tapping
 * opens the full-size image in the `react-photo-view` lightbox
 * (zoom/pan). One `PhotoProvider` per message, mirroring the
 * community-channels attachment lightbox.
 */
export function ImageMessage({ message, isOwn }: Props) {
  const { src, fullSrc, size } = useImageInfo(message.content);

  if (!src) {
    return (
      <MessageBubbleShell message={message} isOwn={isOwn}>
        <div className="bg-surface-3 text-content-2 px-3.5 py-2 text-xs italic">
          Image unavailable
        </div>
      </MessageBubbleShell>
    );
  }

  return (
    <MessageBubbleShell message={message} isOwn={isOwn} bare>
      <PhotoProvider>
        <PhotoView src={fullSrc ?? src}>
          <button
            type="button"
            className="block cursor-zoom-in overflow-hidden rounded-2xl"
            style={{ width: size.width, height: size.height }}
          >
            {/* Plain <img> rather than next/image — remote firebase
                storage URLs aren't in the Next image config; intrinsic
                sizing is already locked via the container. */}
            <img
              src={src}
              alt=""
              width={size.width}
              height={size.height}
              className="bg-surface-1 h-full w-full object-cover"
              loading="lazy"
            />
          </button>
        </PhotoView>
      </PhotoProvider>
    </MessageBubbleShell>
  );
}

function useImageInfo(content: ImageContent): {
  src: string | undefined;
  fullSrc: string | undefined;
  size: { width: number; height: number };
} {
  const size = useMemo(
    () => getMessageMediaSize(content?.width, content?.height),
    [content?.width, content?.height],
  );
  if ('localFileUri' in content) {
    return { src: content.localFileUri, fullSrc: content.localFileUri, size };
  }
  return {
    src: content.thumbnailUrl ?? content.imageUrl,
    fullSrc: content.imageUrl,
    size,
  };
}
